import { errorMessage } from '../../i18n/errorMessages';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useState } from 'react';
import { Button, Input } from 'antd';
import { ApiError, updateGuild } from '../../lib/api';
import { useGuildId } from '../../hooks/useGuildId';
import { useProjectAuthority } from '../../hooks/useProjectAuthority';
import { useServerStore } from '../../stores/useServerStore';
import styles from './ServerSettings.module.css';
import surface from '../ProjectSurface.module.css';
import ProjectPageHeader from '../ProjectPageHeader';
import { ZoneManagementDialog } from '../ZoneManagementDialog';

export default function ServerProfilePanel() {
  const { t, i18n } = useLingui();
  const guildId = useGuildId();
  const authority = useProjectAuthority(guildId);
  const server = useServerStore((state) => state.guilds.find((guild) => guild.id === guildId));
  const replaceGuild = useServerStore((state) => state.replaceGuild);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<MessageDescriptor | null>(null);
  const [managingZones, setManagingZones] = useState(false);
  const canUpdate = authority.ready && authority.canManageProject;

  useEffect(() => {
    setName(server?.name ?? '');
    setIcon(server?.icon ?? '');
    setError(null);
  }, [guildId, server?.name, server?.icon]);

  useEffect(() => {
    if (!canUpdate) setManagingZones(false);
  }, [canUpdate]);

  if (!server || !authority.ready)
    return (
      <div className="no-channel" role="status">
        <Trans>Loading Project settings…</Trans>
      </div>
    );

  if (!canUpdate) {
    return (
      <section
        className={`${surface.pageContainer} ${styles.denied}`}
        aria-label={t`Project settings access denied`}
      >
        <h1 className={surface.pageTitle}>
          <Trans>Project Settings</Trans>
        </h1>
        <p>
          <Trans>You don’t have permission to update this Project.</Trans>
        </p>
      </section>
    );
  }

  const save = async () => {
    const targetGuildId = guildId;
    const normalizedName = name.trim();
    const normalizedIcon = icon.trim();
    if (!normalizedName) {
      setError(msg`Project name is required.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await updateGuild(targetGuildId, {
        name: normalizedName,
        icon: normalizedIcon,
      });
      // A response for a Server left while saving must never overwrite the
      // selected Server's view. The store still reconciles the exact ID.
      replaceGuild(updated);
    } catch (err) {
      setError(errorMessage(err, msg`Could not save Project settings.`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.profile} aria-label={t`Project profile`}>
      <ProjectPageHeader title={t`Settings`} />

      <section
        className={`${surface.pageContainer} ${surface.section} ${styles.section}`}
        aria-labelledby="project-profile-title"
      >
        <div>
          <h2 className={styles.sectionTitle} id="project-profile-title">
            <Trans>General</Trans>
          </h2>
          <p className={styles.sectionDescription}>
            <Trans>Update the name and icon used for this Project.</Trans>
          </p>
        </div>
        <div className={styles.profileSummary}>
          <span className={styles.avatar}>{icon || name[0]?.toUpperCase() || 'S'}</span>
          <div className={styles.summaryCopy}>
            <div className={styles.serverName}>{server.name}</div>
            <div className={styles.description}>
              <Trans>Project profile</Trans>
            </div>
          </div>
        </div>

        <div className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="server-name">
              <Trans>PROJECT NAME</Trans>
            </label>
            <Input
              className={styles.fieldControl}
              id="server-name"
              value={name}
              maxLength={100}
              onChange={(event) => setName(event.target.value)}
              disabled={saving}
            />
            <p className={styles.hint}>
              <Trans>This name appears in the Project rail and switcher.</Trans>
            </p>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="server-icon">
              <Trans>ICON</Trans>
            </label>
            <Input
              className={styles.fieldControl}
              id="server-icon"
              value={icon}
              maxLength={500}
              onChange={(event) => setIcon(event.target.value)}
              disabled={saving}
              placeholder={t`Emoji or short text`}
            />
          </div>
        </div>
        {error && (
          <p className={styles.error} role="alert">
            {i18n._(error)}
          </p>
        )}
        <div className={styles.actions}>
          <Button
            type="primary"
            onClick={() => void save()}
            loading={saving}
            disabled={name.trim() === server.name && icon.trim() === server.icon}
          >
            <Trans>Save changes</Trans>
          </Button>
        </div>
      </section>
      <section
        className={`${surface.pageContainer} ${surface.section} ${styles.section}`}
        aria-labelledby="project-zones-title"
      >
        <div>
          <h2 className={styles.sectionTitle} id="project-zones-title">
            <Trans>Zones</Trans>
          </h2>
          <p className={styles.sectionDescription}>
            <Trans>
              Group related channels in the sidebar. Channel access and membership remain
              independent.
            </Trans>
          </p>
        </div>
        <p className={styles.hint}>
          <Trans>
            Default is the virtual location for channels with no named Zone. Project members can see
            all named Zones, including empty ones.
          </Trans>
        </p>
        <div className={styles.actions}>
          <Button onClick={() => setManagingZones(true)}>
            <Trans>Manage Zones</Trans>
          </Button>
        </div>
      </section>
      <ZoneManagementDialog open={managingZones} onClose={() => setManagingZones(false)} />
    </section>
  );
}
